import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../../environments/environment';
import * as Sentry from '@sentry/angular';
import * as _ from 'lodash';
import { NgrxService } from './ngrx.service';
import * as AuthActions from './../../../app/@store/auth/auth.actions';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';
import { ActionsSubject, Store } from '@ngrx/store';
import { getFullSession, getUser } from './../../../app/@store/user/user.selectors';
import { debounceTime, filter, take } from 'rxjs/operators';
import { getPartner } from './../../../app/@store/partner/partner.selectors';
import { PublicApiErrorResponse, PublicPartnerApiService } from './publicpartnerapi.service';
import { ofType } from '@ngrx/effects';
import { globalFailure, translationMissingAction } from './../../../app/@store/common.actions';



export interface TheError {
  status: number;
  message: string;
  url: string | null;
  id?: string;
  module?: string;
  headers?: Headers | HttpHeaders;
  method?: string;
  failureType?: string;
  originalError?: any;
  originalErrorType?: string;
}

/**
 * Error handler service; handles the sentry integration
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private sentryActive = false;

  private logErrorDebounce?: number;

  constructor(
    private readonly ngrxService: NgrxService,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private readonly store: Store,
    private publicApi: PublicPartnerApiService,
    private actions$: ActionsSubject
  ) {
    this.store
      .select(getFullSession)
      .pipe(
        filter(fullSession => !!fullSession.user && !!fullSession.partner),
        take(1)
      )
      .subscribe(fullSession => {
        this.setupSentry(fullSession.partner!, fullSession.user!);
      });

    this.publicApi.addLogger(this.logApiErrorPublic);

    this.actions$.pipe(
      ofType(translationMissingAction)
    ).subscribe(action => {
      this.captureMessage('Missing Translation: ' + action.key + ' (language: ' + action.language + ')');
    });

    this.actions$.pipe(
      ofType(globalFailure),
      filter(action => {
        if (action.whitelist?.length && action.error instanceof HttpErrorResponse) {
          if (action.whitelist.indexOf(action.error.status) >= 0) {
            return false; // this statuscode is whitelisted
          }
        }
        return true;
      }),
      debounceTime(100),
    ).subscribe(action => {

      if (action.error instanceof HttpErrorResponse) {
        this.handleHttpErroreResponse(action.error);

      } else {
        const theError: TheError = {
          message: action.error?.message || 'Unknown error',
          status: 0,
          url: '?'
        };

        this.logApiError(theError);
      }
    });

  }

  handleHttpErroreResponse(error: HttpErrorResponse) {
    const theError: TheError = {
      message: error?.message || 'Unknown error',
      status: 0,
      url: '?'
    };

    theError.status = error.status;
    theError.failureType = 'json';
    theError.url = error.url;
    theError.message = error.message;
    theError.originalError = error.error;
    // theError.headers = new HttpHeaders(headerMap); TODO: does not work to pass this in an action ("Object is not extensible" error)

    if (error?.error?.errors?.length) {
      const detailError = (error.error.errors as any[])[error.error.errors.length - 1];
      if (detailError.detail !== undefined && typeof detailError.detail === 'string') {
        theError.message = detailError.detail;
      }
    }

    this.logApiError(theError);

  }

  private setupSentry(partner: ReturnType<typeof getPartner>, user: ReturnType<ReturnType<typeof getUser>>) {
    if (user && document.location.hostname !== 'localhost' && environment.sentry_logging === true && environment.sentry_endpoint) {
      // Sentry.init is done in main.ts!

      const scope = Sentry.getCurrentScope();
      scope.setUser({
        email: user.email,
        id: user.object_uuid
      });
      scope.setTag('ctrl-plane', '<!--#echo var="ssiCTRL_PLANE"-->'); // that ssi string is replaced in the compiled javascript by nginx on runtime
      if (partner?.object_uuid) {
        scope.setTag('partner-uuid', partner.object_uuid);
        if (partner.package) {
          scope.setTag('package', partner.package);
        }
      }
      this.sentryActive = true;

    }

  }

  captureException(error: Error) {
    if (this.sentryActive) {
      return Sentry.captureException(error);
    }
  }

  captureMessage(message: string) {
    if (this.sentryActive) {
      return Sentry.captureMessage(message);
    }
  }

  logApiErrorPublic = async (_e: PublicApiErrorResponse) => {
    const theError: TheError = {
      status: 0,
      message: '',
      url: '',
      originalErrorType: 'PublicApiErrorResponse'
    };

    theError.status = _e.status;
    theError.message = _e.message;
    theError.headers = _e.headers;
    theError.method = _e.method;
    theError.url = _e.url;


    if (_e.error?.errors) {
      _.forEach(_e.error?.errors, errorInfo => {
        if (errorInfo.title) {
          theError.message += "\n" + errorInfo.title;
        }
      });
    }

    theError.originalError = _e;

    await this.logApiError(theError);
  };


  async logApiError(theError: TheError) {
    if (theError.status === 0 || theError.status === 423 || (theError.status === 404 && theError.method === 'GET')) {
      return;
    }

    // ignore network errors...
    if (
      typeof theError.message === 'string' &&
      (theError.message.indexOf('The network connection was lost') !== -1 ||
        theError.message.indexOf('NetworkError when attempting to fetch resource') !== -1 ||
        theError.message.indexOf('Network failure') !== -1)
    ) {
      return;
    }

    if (this.logErrorDebounce !== undefined) {
      return;
    }
    this.logErrorDebounce = window.setTimeout(() => {
      this.logErrorDebounce = undefined;
    }, 200);



    if (theError.status === 401 && (theError.module === undefined || theError.module !== 'Session') && theError.url?.indexOf('smtp_config_test') === -1) {
      // 401 on a GET and not supressed -> redirect to login

      this.ngrxService.callBasicActionWithCallbacks({
        actionToCall: AuthActions.reValidateAuth(),
        successAction: AuthActions.validateAuthSuccess,
        failureAction: AuthActions.validateAuthFailure,
        successCallback: () => {
          // If everything is file, we do nothing
        },
        failureCallback: () => { }
      });
    }

    let requrestID: any = false;

    if (theError.headers) {
      if (theError.headers.has('x-request-id')) {
        requrestID = theError.headers.get('x-request-id');
      } else if (theError.headers.has('x-request-id-0')) {
        requrestID = theError.headers.get('x-request-id-0');
      }
    }


    let errorName = 'API Error';

    errorName += ': ';
    if (theError.method) {
      errorName += theError.method + ' | ';
    }

    if (theError.status !== undefined) {
      errorName += theError.status;
    }

    if (theError.failureType && theError.failureType == 'json') {
      errorName += ' (JSON parse failed)';
    }

    let subline = theError.url?.split('?')[0];
    let extractedUUID: string | undefined;
    if (subline) {
      if (subline.match(/(\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b)/g)) {
        extractedUUID = RegExp.$1;
        subline = subline.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/g, 'UUID');
      }
    }

    const error = new Error(subline);
    error.name = errorName;
    let responseBody: string;
    let requestBody: string;
    if (theError.originalErrorType === 'GSError' && theError.originalError.result) {
      if (theError.originalError.result.response instanceof Response) {
        responseBody = await theError.originalError.result.response.clone().text();
      }

      if (theError.originalError.result.request instanceof Request) {
        requestBody = await theError.originalError.result.request.clone().text();
      }
    }

    if (this.sentryActive) {
      Sentry.withScope(scope => {
        if (theError.id) {
          scope.setTag('error_id_displayed', theError.id);
        }
        scope.setExtra('error', theError.originalError);

        if (theError.originalErrorType === 'GSError') {
          scope.setExtra('error.result', theError.originalError.result);
        }

        if (extractedUUID) {
          scope.setTag('extracted_uuid_from_url', extractedUUID);
        }
        if (requrestID) {
          scope.setTag('request_id', requrestID);
        }

        if (theError.originalErrorType === 'GSError' && theError.originalError.result && theError.originalError.result.response instanceof Response) {
          scope.setExtra('response_stringified', this.stringifyResponseRequest(theError.originalError.result.response.clone()));
          if (responseBody) {
            scope.setExtra('response_body', responseBody);
          }
        }
        if (theError.originalErrorType === 'GSError' && theError.originalError.result && theError.originalError.result.request instanceof Request) {
          scope.setExtra('request_stringified', this.stringifyResponseRequest(theError.originalError.result.request.clone()));
          if (requestBody) {
            scope.setExtra('request_body', requestBody);
          }
        }

        Sentry.captureException(error);
      });
    }

    // show toast
    if (theError.status >= 400 && theError.status < 600) {
      let detailText = '';

      switch (theError.status) {
        case 400:
          detailText = this.translate.instant('request_error_format');
          break;

        case 401:
        case 403:
          detailText = this.translate.instant('request_error_no_access');
          break;

        case 404:
          detailText = this.translate.instant('request_error_not_found');
          break;

        case 405:
          detailText = this.translate.instant('request_error_not_allowed');
          break;

        case 409:
          detailText = this.translate.instant('request_error_conflict');
          break;

        default:
          detailText = this.translate.instant('request_error_general');
          break;
      }

      if (_.has(theError, 'originalError.error.detail')) {
        detailText = theError.originalError.error.detail;

        try {
          const description = this.parseCrapJsonFromAPI(theError.originalError.error.detail);
          if (description.message) {
            detailText = description.message;
          }
        } catch (e) { }
      } else if (theError.message) {
        detailText = theError.message;
      }

      // console.log({
      //   severity: 'error',
      //   summary: this.translate.instant('request_error') + ' #' + (requrestID || theError.id || 'unknown'),
      //   detail: detailText + ((detailText[detailText.length - 1] == '.') ? '' : '.') ,
      //   life: 8000
      // });

      this.toastService.add({
        severity: 'error',
        summary: this.translate.instant('request_error'),
        detail: detailText + (detailText[detailText.length - 1] == '.' ? '' : '.'),
        life: 8000
      });
    }
  }

  /**
 * Try to parse the API response, claiming to be JSON but actually is CrapSON
 * @param crapJSON
 */
  private parseCrapJsonFromAPI(crapJSON: string): any {
    let object: any = '';
    let openSurrounder;
    let currentSurroundedString = '';
    let isKey = true;
    let currentKey;
    let lastWasEscape = false;
    let jsonStarted = false;
    for (let i = 0; i < crapJSON.length; ++i) {
      const c = crapJSON[i];

      let normalChar = true;
      if (!jsonStarted && c === '{') {
        jsonStarted = true;
        object = {};
        continue;
      }
      if (!jsonStarted) {
        object += c;
        continue;
      }
      if (c.match(/\s/) && !openSurrounder) {
        // whitespace, ignore
        continue;
      }

      switch (c) {
        case '}':
          if (!openSurrounder) {
            jsonStarted = false;
            normalChar = false;
          }
          break;

        case '\\':
          if (!lastWasEscape) {
            lastWasEscape = true;
            normalChar = false;
          }
          break;

        case '"':
        case "'":
          if (!lastWasEscape) {
            if (!openSurrounder) {
              // something was opened
              openSurrounder = c;
              normalChar = false;
            } else if (openSurrounder === c) {
              // something was closed, so ready read
              // key or value?
              if (isKey) {
                currentKey = currentSurroundedString;
              } else if (currentKey !== undefined) {
                object[currentKey] = currentSurroundedString;
                currentKey = undefined;
              }
              currentSurroundedString = '';
              openSurrounder = undefined;

              normalChar = false;
            }
          } else {
            lastWasEscape = false;
          }
          break;

        case ':':
          if (!openSurrounder) {
            isKey = !isKey;
            normalChar = false;
          }
          break;

        case ',':
          if (!openSurrounder) {
            // next key value pair

            if (currentKey) {
              // was an unsurrounded value
              if (currentSurroundedString.match(/^[0-9]+$/)) {
                object[currentKey] = parseInt(currentSurroundedString, 10);
              } else if (currentSurroundedString === 'true') {
                object[currentKey] = true;
              } else if (currentSurroundedString === 'false') {
                object[currentKey] = false;
              }
            }

            isKey = true;
            currentKey = undefined;
            currentSurroundedString = '';

            normalChar = false;
          }
          break;

        default:
          if (!(openSurrounder || (!isKey && c.match(/^[0-9]+$/)))) {
            throw new Error('Even crappy JSON parser cannot parse: Invalid character "' + c + '" at position ' + i);
          }
      }
      if (normalChar) {
        if (openSurrounder || (!isKey && c.match(/^[0-9]+$/))) {
          currentSurroundedString += c;
        }
      }
    }

    return object;
  }

  private stringifyResponseRequest(object: Response | Request): any {
    const tmp: Record<string, any> = {};
    _.forEach(object, (val, x) => {
      if (val instanceof Headers) {
        tmp[x] = {};

        val.forEach((_h, _k) => {
          tmp[x][_k] = _h;
        });
      } else if (val instanceof Request) {
        tmp[x] = this.stringifyResponseRequest(val);
      } else if (
        ["string", "number", "object", "boolean"].indexOf(typeof val) >= 0
      ) {
        tmp[x] = val;
      }
    })

    return tmp;
  }


  get isSentryActive() {
    return this.sentryActive;
  }
}
